import { useEffect, useId, useRef } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

/** Une clases ignorando las vacías, para no arrastrar una dependencia por esto. */
export function clases(...partes: (string | false | null | undefined)[]): string {
  return partes.filter(Boolean).join(' ');
}

type VarianteBoton = 'primario' | 'secundario' | 'silencioso' | 'peligro';

const BOTON: Record<VarianteBoton, string> = {
  primario: 'bg-marca-600 text-white hover:bg-marca-700 active:bg-marca-800 shadow-panel',
  secundario:
    'bg-white text-tinta-700 border border-papel-300 hover:bg-papel-50 active:bg-papel-100',
  silencioso: 'bg-transparent text-tinta-600 hover:bg-papel-200/70 active:bg-papel-300/60',
  peligro: 'bg-alerta-500 text-white hover:bg-alerta-700 active:bg-alerta-700',
};

type PropsBoton = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBoton;
  /** Ocupa todo el ancho: en teléfono es lo que corresponde para la acción principal. */
  ancho?: boolean;
  cargando?: boolean;
};

export function Boton({
  variante = 'secundario',
  ancho = false,
  cargando = false,
  className,
  children,
  disabled,
  ...resto
}: PropsBoton) {
  return (
    <button
      type="button"
      {...resto}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      className={clases(
        'inline-flex min-h-tactil items-center justify-center gap-2 rounded-lg px-4 text-sm',
        'font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        BOTON[variante],
        ancho && 'w-full',
        className,
      )}
    >
      {cargando && <Girador />}
      {children}
    </button>
  );
}

function Girador() {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

type PropsCampo = InputHTMLAttributes<HTMLInputElement> & {
  etiqueta: string;
  /** Mensaje del servidor o de la validación local; se anuncia al lector de pantalla. */
  error?: string | null;
  ayuda?: string;
};

export function Campo({ etiqueta, error, ayuda, className, id, ...resto }: PropsCampo) {
  const generado = useId();
  const idCampo = id ?? generado;
  const idAyuda = `${idCampo}-ayuda`;

  return (
    <div className="space-y-1">
      <label htmlFor={idCampo} className="etiqueta-campo">
        {etiqueta}
      </label>
      <input
        id={idCampo}
        {...resto}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || ayuda ? idAyuda : undefined}
        className={clases('campo', error && 'border-alerta-500 focus:ring-alerta-500/30', className)}
      />
      {(error || ayuda) && (
        <p id={idAyuda} className={clases('text-dato', error ? 'text-alerta-700' : 'text-tinta-400')}>
          {error ?? ayuda}
        </p>
      )}
    </div>
  );
}

type PropsSelector = SelectHTMLAttributes<HTMLSelectElement> & {
  etiqueta: string;
  error?: string | null;
};

export function Selector({ etiqueta, error, className, id, children, ...resto }: PropsSelector) {
  const generado = useId();
  const idCampo = id ?? generado;

  return (
    <div className="space-y-1">
      <label htmlFor={idCampo} className="etiqueta-campo">
        {etiqueta}
      </label>
      <select
        id={idCampo}
        {...resto}
        aria-invalid={error ? true : undefined}
        className={clases('campo', error && 'border-alerta-500', className)}
      >
        {children}
      </select>
      {error && <p className="text-dato text-alerta-700">{error}</p>}
    </div>
  );
}

export function Panel({
  titulo,
  descripcion,
  accion,
  children,
  className,
}: {
  titulo?: string;
  descripcion?: string;
  accion?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clases('panel', className)}>
      {(titulo || accion) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-papel-200 px-4 py-3">
          <div>
            {titulo && <h2 className="text-base font-semibold text-tinta-900">{titulo}</h2>}
            {descripcion && <p className="mt-0.5 text-dato text-tinta-500">{descripcion}</p>}
          </div>
          {accion}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

type Tono = 'neutro' | 'exito' | 'aviso' | 'alerta' | 'marca';

const TONO: Record<Tono, string> = {
  neutro: 'bg-papel-200 text-tinta-600',
  exito: 'bg-exito-50 text-exito-700 ring-1 ring-inset ring-exito-200',
  aviso: 'bg-aviso-50 text-aviso-700 ring-1 ring-inset ring-aviso-200',
  alerta: 'bg-alerta-50 text-alerta-700 ring-1 ring-inset ring-alerta-200',
  marca: 'bg-marca-50 text-marca-700 ring-1 ring-inset ring-marca-200',
};

export function Insignia({
  tono = 'neutro',
  children,
  className,
}: {
  tono?: Tono;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clases(
        'inline-flex items-center rounded-full px-2 py-0.5 text-dato font-medium',
        TONO[tono],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Estado vacío que enseña el siguiente paso, como pide el encargo: nunca una pantalla en
 * blanco que deje al usuario sin saber qué hacer.
 */
export function Vacio({
  titulo,
  detalle,
  accion,
}: {
  titulo: string;
  detalle?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      <p className="text-sm font-medium text-tinta-700">{titulo}</p>
      {detalle && <p className="max-w-sm text-dato text-tinta-500">{detalle}</p>}
      {accion}
    </div>
  );
}

export function Cargando({ que = 'los datos' }: { que?: string }) {
  return (
    <div role="status" className="flex items-center gap-3 px-4 py-10 text-tinta-500">
      <Girador />
      <span className="text-sm">Cargando {que}…</span>
    </div>
  );
}

/** Error de carga con el mensaje que devolvió el servidor, sin adornarlo. */
export function ErrorEnPantalla({
  mensaje,
  reintentar,
}: {
  mensaje: string;
  reintentar?: () => void;
}) {
  return (
    <div role="alert" className="rounded-panel bg-alerta-50 px-4 py-5 text-center">
      <p className="text-sm font-medium text-alerta-700">{mensaje}</p>
      {reintentar && (
        <Boton className="mt-3" onClick={reintentar}>
          Reintentar
        </Boton>
      )}
    </div>
  );
}

/**
 * Aviso efímero. El encargo pide preferir deshacer antes que confirmar, así que el aviso
 * puede llevar una acción de deshacer en lugar de un diálogo previo.
 */
export function Aviso({
  tono = 'exito',
  children,
  accion,
  alCerrar,
  className,
}: {
  tono?: Tono;
  children: ReactNode;
  accion?: ReactNode;
  alCerrar?: () => void;
  className?: string;
}) {
  return (
    <div
      role={tono === 'alerta' ? 'alert' : 'status'}
      className={clases(
        'flex flex-wrap items-center justify-between gap-3 rounded-panel px-4 py-3 text-sm',
        TONO[tono],
        className,
      )}
    >
      <span>{children}</span>
      <span className="flex items-center gap-2">
        {accion}
        {alCerrar && (
          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar aviso"
            className="rounded px-2 py-1 text-dato underline underline-offset-2"
          >
            Cerrar
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Panel lateral para formularios. Se usa en lugar de un diálogo modal porque en tablet un
 * modal centrado tapa el contexto y obliga a cerrarlo para mirar el dato de atrás.
 */
export function Cajon({
  abierto,
  titulo,
  alCerrar,
  children,
  pie,
}: {
  abierto: boolean;
  titulo: string;
  alCerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
}) {
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) {
      return;
    }

    const alTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        alCerrar();
      }
    };

    document.addEventListener('keydown', alTeclado);
    // El foco entra al panel para que el teclado no quede detrás, en la página.
    contenedor.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();

    return () => document.removeEventListener('keydown', alTeclado);
  }, [abierto, alCerrar]);

  if (!abierto) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="absolute inset-0 bg-tinta-900/30"
        onClick={alCerrar}
        aria-hidden="true"
      />
      <div
        ref={contenedor}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="relative flex h-full w-full max-w-md flex-col bg-papel-50 shadow-elevado"
      >
        <header className="flex items-center justify-between border-b border-papel-300 px-4 py-3">
          <h2 className="text-base font-semibold text-tinta-900">{titulo}</h2>
          <Boton variante="silencioso" onClick={alCerrar} aria-label="Cerrar">
            ✕
          </Boton>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {pie && <footer className="border-t border-papel-300 px-4 py-3">{pie}</footer>}
      </div>
    </div>
  );
}

/** Dato con su rótulo, la unidad mínima de las fichas. */
export function Dato({
  rotulo,
  children,
  className,
}: {
  rotulo: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="etiqueta-campo">{rotulo}</dt>
      <dd className="mt-0.5 text-sm text-tinta-800">{children}</dd>
    </div>
  );
}

/** Tabla que en teléfono se desplaza sola en lugar de romper el ancho de la página. */
export function Desplazable({ children }: { children: ReactNode }) {
  return <div className="-mx-4 overflow-x-auto px-4">{children}</div>;
}
